import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useParams, useHistory, NavLink } from 'react-router-dom';
import { DateTime } from 'luxon';

// material-ui
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Link from '@material-ui/core/Link';
import Grid from '@material-ui/core/Grid';
import Alert from '@material-ui/lab/Alert';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import Avatar from '@material-ui/core/Avatar';
import Pagination from '@material-ui/lab/Pagination';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Dialog from '@material-ui/core/Dialog';
import Tooltip from '@material-ui/core/Tooltip';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import UserIcon from '@material-ui/icons/Person';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import CardGiftcardIcon from '@material-ui/icons/CardGiftcard';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';

import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';

import Loading from 'app/components/loaders/Loading';
import EditInvitationDialog from 'app/components/event/EditInvitationDialogAdmin';
import EditDateDialog from 'app/components/event/EditDateDialog';
import ChargePaymentMethodDialog from 'app/components/accounting/payments/ChargePaymentMethodDialog';
import RedButton from 'app/components/buttons/RedButton';

const useStyles = makeStyles(theme => ({
    container: {
        padding: theme.spacing(3),
        position: 'relative'
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    marginRight: {
        marginRight: theme.spacing(3)
    },
    enterButton: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
        marginBottom: theme.spacing(3)
    },
    table: {
        minWidth: '100%',
    },
    tableWrapper: {
        overflowX: 'auto',
    },
    tableCell: {
        whiteSpace: "nowrap"
    },
    empty: {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3)
    },
    emptyContainer: {
        height: '70vh',
        position: 'relative'
    }
}));

export default function EditInvitation(props) {

    const { event, getEvent } = props;

    const [ invitation, setInvitation ] = useState(null);
    const [ outstandingInvoices, setOutstandingInvoices ] = useState([]);
    const [ activeInvoice, setActiveInvoice ] = useState(null);

    const [ editInvitationDialogOpen, setEditInvitationDialogOpen ] = useState(false);
    const [ chargePaymentMethodDialogOpen, setChargePaymentMethodDialogOpen ] = useState(false);
    const [ chargeInviterPaymentMethodDialogOpen, setChargeInviterPaymentMethodDialogOpen ] = useState(false);
    const [ editDateDialogOpen, setEditDateDialogOpen ] = useState(false);
    const [ deleteInvitationDialogOpen, setDeleteInvitationDialogOpen ] = useState(false);

    const [ activeDate, setActiveDate ] = useState(null);
    const [ datesPage, setDatesPage ] = useState(1);
    const [ filteredDates, setFilteredDates ] = useState([]);
    const [ dateSearchValue, setDateSearchValue ] = useState("");
    const [ urlToCopyText, setUrlToCopyText ] = useState("Copy to Clipboard");

    const [ loading, setLoading ] = useState(false);

    const api = useApi();
    const { app } = useApp();
    const classes = useStyles();
    const params = useParams();
    const history = useHistory();

    const getInvitation = () => {
        setLoading(true);
        api.get(`/admin/invitation/${params.id}`)
            .then(res => {
                setInvitation(res.invitation);
                setOutstandingInvoices(res.outstanding_invoices);
            })
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        getInvitation();
    }, []);

    useEffect(() => {
        if (invitation) {
            setFilteredDates(invitation.dates);
            setDateSearchValue("");
            setDatesPage(1);
        }
    }, [invitation]);

    // Setter Functions

    const handleChangeDatesPage = (event, newPage) => {
        setDatesPage(newPage)
    };

    const handleDateSearchValue = event => {
        event.preventDefault();

        const newSearchValue = event.target.value

        let newFilteredDates = invitation.dates.filter((d) => {
            let match = false;
            if (d.full_name !== null) {
                let m = d.name.toLowerCase().includes(newSearchValue.toLowerCase());
                if (m) match = true;
            }
            return match;
        })

        setFilteredDates(newFilteredDates);
        setDateSearchValue(newSearchValue);
        setDatesPage(1);
    };

    // Poseter Functions

    const postEnter = () => {
        setLoading(true);
        api.post(`/admin/invitation/${invitation.id}/enter`, {})
            .then(() => {
                getInvitation();
                setLoading(false);
                // history.push(`/admin/events/${event.id}/guestlist`)
            })
            .finally(() => setLoading(false))
    }

    const postExit = () => {
        setLoading(true);
        api.post(`/admin/invitation/${invitation.id}/exit`, {})
            .then(() => {
                getInvitation();
                setLoading(false);
                // history.push(`/admin/events/${event.id}/guestlist`)
            })
            .finally(() => setLoading(false))
    }

    const postPending = () => {
        setLoading(true);
        api.post(`/admin/invitation/${invitation.id}/pending`, {})
            .then(res => {
                setInvitation(res.invitation);
                getEvent(true);
            })
            .finally(() => setLoading(false))

    }

    const postAccept = () => {

        if (invitation.status === 'accepted') {
            postPending();
        } else {
            setLoading(true);
            api.post(`/admin/invitation/${invitation.id}/accept`, {})
                .then(res => {
                    setInvitation(res.invitation);
                    getEvent(true);
                })
                .finally(() => setLoading(false))
        }
    }

    const postMaybe = () => {

        if (invitation.status === 'maybe') {
            postPending();
        } else {
            setLoading(true);
            api.post(`/admin/invitation/${invitation.id}/maybe`, {})
                .then(res => {
                    setInvitation(res.invitation);
                    getEvent(true);
                })
                .finally(() => setLoading(false))
        }
    }

    const postWithdraw = () => {

        if (invitation.status === 'withdrawn') {
            postPending();
        } else {
            setLoading(true);
            api.post(`/admin/invitation/${invitation.id}/withdraw`, {})
                .then(res => {
                    setInvitation(res.invitation);
                    getEvent(true);
                })
                .finally(() => setLoading(false))
        }
    }

    const postDecline = () => {
        if (invitation.status === 'declined') {
            postPending();
        } else {
            setLoading(true);
            api.post(`/admin/invitation/${invitation.id}/decline`, {})
                .then(res => {
                    setInvitation(res.invitation);
                    getEvent(true);
                })
                .finally(() => setLoading(false));
        }
    }

    const postFlagged = () => {
        setLoading(true);
        api.post(`/admin/invitation/${invitation.id}/adminFlag`, {})
            .then(res => {
                setInvitation(res.invitation);
                getEvent(true);
            })
            .finally(() => setLoading(false));
        
    }

    const deleteInvitation = () => {
        setLoading(true);
        api.delete(`/admin/invitation/${invitation.id}`)
            .then(() => history.push(`/admin/events/${event.id}/invitations`))
            .finally(() => setLoading(false));
    }

    // Invite Date Dialog Functions ------------------------------------------

    const openEditDateDialog = d => {
        setActiveDate(d);
        setEditDateDialogOpen(true)
    }

    const closeEditDateDialog = () => {
        setEditDateDialogOpen(false);
    }

    const closeEditDateDialogAndRefresh = () => {
        closeEditDateDialog();
        getInvitation();
    }

    const editDateDialogClosed = () => {
        setActiveDate(null);
    }

    // Withdraw Dialog Functions

    const handleDialogOpen = (id) => {
        setWithdrawInvitationID(id)
        setWithdrawDialogOpen(true);
    }

    const handleDialogClose = () => {
        setWithdrawDialogOpen(false);
        setWithdrawInvitationID(null)
    }

    // Edit Dialog Functions

    const openEditInvitationDialog = () => {
        setEditInvitationDialogOpen(true)
    }

    const closeEditInvitationDialog = () => {
        setEditInvitationDialogOpen(false);
    }

    const closeEditInvitationDialogAndRefresh = () => {
        closeEditInvitationDialog();
        getInvitation();
    }

     // Charge Card Dialog --------------------------------------

     const openChargePaymentMethodDialog = (invoice) => {
        setActiveInvoice(invoice);
        setChargePaymentMethodDialogOpen(true);
    }

    const closeChargePaymentMethodDialog = () => {
        setChargePaymentMethodDialogOpen(false);
    }

    const closeChargePaymentMethodDialogAndRefresh = () => {
        closeChargePaymentMethodDialog();
        getInvitation();
        // hydrateApp(true);
        // getUnpaidInvoices();
        // getPayments();
    }

    const chargePaymentMethodDialogClosed = () => {
        setActiveInvoice(null);
    }

     // Charge Inviter Card Dialog --------------------------------------

     const openChargeInviterPaymentMethodDialog = (invoice) => {
        setActiveInvoice(invoice);
        setChargeInviterPaymentMethodDialogOpen(true);
    }

    const closeChargeInviterPaymentMethodDialog = () => {
        setChargeInviterPaymentMethodDialogOpen(false);
    }

    const closeChargeInviterPaymentMethodDialogAndRefresh = () => {
        closeChargeInviterPaymentMethodDialog();
        getInvitation();
        // hydrateApp(true);
        // getUnpaidInvoices();
        // getPayments();
    }

    const chargeInviterPaymentMethodDialogClosed = () => {
        setActiveInvoice(null);
    }

    // Delete Invitation Dialog ----------------------

    const openDeleteInvitationDialog = d => {
        setDeleteInvitationDialogOpen(true);
    }

    const closeDeleteInvitationDialog = () => {
        setDeleteInvitationDialogOpen(false);
    }

    // Constants --------------------------------

    const invitationAlert = useMemo(() => {
        if (invitation) {
            switch (invitation.status) {
                case 'pending':
                    return {
                        severity: 'info',
                        text: 'Invitation is PENDING'
                    };
                case 'maybe':
                    return {
                        severity: 'info',
                        text: 'Invitation has been marked MAYBE'
                    };
                case 'accepted':
                    return {
                        severity: 'success',
                        text: 'Invitation has been marked as ACCEPTED'
                    };
                case 'withdrawn':
                    return {
                        severity: 'warning',
                        text: 'Invitation has been WITHDRAWN by the Inviter'
                    };
                case 'declined':
                    return {
                        severity: 'warning',
                        text: 'Invitation has been marked as DECLINED'
                    };
                case 'pay_later':
                    return {
                        severity: 'warning',
                        text: 'Invitation has been marked as PAY LATER'
                    };
            }
        }

    }, [invitation]);

    const invitationSentBy = useMemo(() => {
        if (invitation) {
            switch (invitation.added_by) {
                case 'group':
                    return (
                        <span>Group Member</span>
                    )
                case 'admin':
                    return (
                        <span>Administrator</span>
                    );
                case 'user':
                    return (
                        <Link component={NavLink} to={`/admin/users/${invitation.invitable_id}`}>
                            <span>{invitation?.invitable?.full_name}</span>
                        </Link>
                    );
                case 'ticket':
                    <span>Ticket</span>
            }
        }

    }, [invitation]);

    const copyToClipboard = () => {
        const range = document.createRange();
        range.selectNode(document.getElementById("url-to-copy"));
        window.getSelection().removeAllRanges(); // clear current selection
        window.getSelection().addRange(range); // to select text
        document.execCommand("copy");
        window.getSelection().removeAllRanges();// to deselect
        setUrlToCopyText('Copied!');
    }

    return invitation ? (
        <div className={classes.container}>
            <Loading isLoading={loading}/>
            <Grid container spacing={3}>
                <Grid item xs={10}>
                    <Alert severity={invitationAlert.severity} >
                        {invitationAlert.text}
                    </Alert>
                    {(invitation.entered_at) &&
                        <Alert severity="success" className={classes.marginTop}>
                            Guest has entered the event.
                        </Alert>
                    }
                    <Grid
                        container
                        className={classes.marginTop}
                        justify="space-between"
                        alignItems="center"
                        >
                        <Grid item>
                            <Typography variant="h5">
                                {invitation.guest.full_name}
                            </Typography>
                        </Grid>
                        <Grid item>
                            {(invitation.invoice) &&
                                <Button
                                    to={`/admin/accounting/invoices/view/${invitation.invoice.id}`}
                                    component={NavLink}
                                    variant="outlined"
                                    color="primary"
                                    className={classes.marginRight}
                                    >
                                    View Ticket Invoice
                                </Button>
                            }
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={openEditInvitationDialog}
                                >
                                Edit Invitation Details
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid container className={classes.marginTop}>
                        <Grid item xs={3}>
                            <strong>
                                Sent By:
                            </strong>
                        </Grid>
                        <Grid item xs={9}>
                            {invitationSentBy}
                        </Grid>
                        <Grid item xs={3}>
                            <strong>
                                Title:
                            </strong>
                        </Grid>
                        <Grid item xs={9}>
                            {invitation.title}
                        </Grid>
                        <Grid item xs={3}>
                            <strong>
                                Message:
                            </strong>
                        </Grid>
                        <Grid item xs={9}>
                            {invitation.message}
                        </Grid>
                        <Grid item xs={3}>
                            <strong>
                                Details:
                            </strong>
                        </Grid>
                        <Grid item xs={9}>
                            {invitation.details}
                        </Grid>
                        {(invitation.ticket) &&
                            <>
                                <Grid item xs={3}>
                                    <strong>
                                        Ticket Type:
                                    </strong>
                                </Grid>
                                <Grid item xs={9}>
                                    {(invitation.ticket) ? (
                                        <>{invitation.ticket.title}<strong> - ${invitation.ticket && invitation.ticket.price}</strong></>
                                    ) : (
                                        <>No Ticket Selected</>
                                    )}
                                </Grid>
                            </>
                        }
                    </Grid>
                    {(outstandingInvoices.length > 0) ? (
                        <div className={classes.marginTop}>
                            <Alert severity="warning">
                                This guest has outstanding invoices!
                            </Alert>
                            <div className={classes.tableWrapper}>
                                <Table className={classes.table}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell className={classes.tableCell}>
                                                Invoice #
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                Description
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                Due On
                                            </TableCell>
                                            <TableCell className={classes.tableCell} align="right">
                                                Amount
                                            </TableCell>
                                            <TableCell className={classes.tableCell} align="right">
                                                Actions
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {outstandingInvoices.map(invoice => (
                                            <TableRow key={invoice.id}>
                                                <TableCell className={classes.tableCell}>
                                                    {invoice.invoice_number}
                                                </TableCell>
                                                <TableCell className={classes.tableCell}>
                                                    {invoice.description}
                                                </TableCell>
                                                <TableCell className={classes.tableCell}>
                                                    <Typography
                                                        variant="subtitle2"
                                                        color={DateTime.fromISO(invoice.due_by) < DateTime.local() ?"error" : "textSecondary"}
                                                        >
                                                        {DateTime.fromISO(invoice.due_by).toLocaleString(DateTime.DATE_MED)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell className={classes.tableCell} align="right">
                                                    ${invoice.amount_f}
                                                </TableCell>
                                                <TableCell className={classes.tableCell} align="right">
                                                    <IconButton
                                                        className={classes.marginRight}
                                                        disabled={!app.club.meta.payment_methods.includes('stripe')}
                                                        onClick={() => openChargePaymentMethodDialog(invoice)}
                                                        >
                                                        <CreditCardIcon size="small"/>
                                                    </IconButton>
                                                    {(invitation.invitable_type === 'App\\Models\\User') &&
                                                        <IconButton
                                                            className={classes.marginRight}
                                                            disabled={!app.club.meta.payment_methods.includes('stripe')}
                                                            onClick={() => openChargeInviterPaymentMethodDialog(invoice)}
                                                            >
                                                            <CardGiftcardIcon size="small"/>
                                                        </IconButton>
                                                    }
                                                    <IconButton
                                                        to={`/admin/accounting/invoices/view/${invoice.id}`}
                                                        component={NavLink}
                                                        >
                                                        <KeyboardArrowRightIcon size="small"/>
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                    ) : (
                        <Alert severity="info" className={classes.marginTop}>
                            This guest has no outstanding invoices.
                        </Alert>
                    )}
                    <Grid
                        container
                        spacing={3}
                        justify="center"
                        className={classes.marginTop}
                        >
                        <Grid item xs={12}>
                            <Grid
                                container
                                justify="space-between"
                                alignItems="center"
                                className={classes.marginBottom}
                                >
                                <Grid item>
                                    <Typography
                                        variant="h6"
                                        >
                                        Dates
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <input autoComplete="false" name="hidden" type="text" style={{ display: 'none' }}/>
                                    <TextField
                                        id="dates-search"
                                        label="Search Dates"
                                        type="search"
                                        size="small"
                                        variant="filled"
                                        value={dateSearchValue}
                                        fullWidth
                                        margin="dense"
                                        onChange={handleDateSearchValue}
                                        InputProps={{ className: classes.bottomRadius, disableUnderline: true }}
                                        color="secondary"
                                    />
                                </Grid>
                                <Grid item>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => openEditDateDialog(null)}
                                        >
                                        Add Date
                                    </Button>
                                </Grid>
                            </Grid>
                            <div className={classes.userContainer}>
                                {filteredDates.length > 0 ? filteredDates.slice(((datesPage-1) * 5), (((datesPage - 1) * 5) + 5)).map(d => (
                                    <Card key={d.id} className={classes.marginBottom}>
                                        <CardHeader
                                            avatar={
                                                <Avatar arai-label="guest" className={classes.clubAvatar} >
                                                    <UserIcon />
                                                </Avatar>
                                            }
                                            title={d.name}
                                            subheader={`Added ${DateTime.fromISO(d.created_at).toLocaleString(DateTime.DATE_MED)}`}
                                            classes={{ action: classes.cardAction }}
                                            action={
                                                <Button
                                                    size="small"
                                                    onClick={() => openEditDateDialog(d)}
                                                    >
                                                    Edit
                                                </Button>
                                            }
                                        />
                                    </Card>
                                )) : (
                                    <Typography
                                        align="center"
                                        color="textSecondary"
                                        variant="subtitle2"
                                        className={classes.empty}
                                        >
                                        You have not yet invited any dates. Click <strong>ADD DATE</strong> to add a date to your invitation.
                                    </Typography>
                                )}
                            </div>
                            {(filteredDates.length > 5) &&
                                <Pagination
                                    count={Math.ceil((filteredDates.length) / 5)}
                                    page={datesPage}
                                    onChange={handleChangeDatesPage}
                                    className={classes.pagination}
                                />
                            }
                        </Grid>
                    </Grid>
                    <Typography color="textSecondary" variant="subtitle2" gutterBottom>
                        Guest Form Link:
                    </Typography>
                    <Tooltip title={urlToCopyText}>
                        <Link onClick={copyToClipboard} href="#">
                            <span id="url-to-copy">{invitation.guest_form_url}</span>
                        </Link>
                    </Tooltip>
                </Grid>
                <Grid item xs={2}>
                    <Button
                        fullWidth
                        size="large"
                        className={classes.enterButton}
                        onClick={invitation.entered_at ? postExit : postEnter}
                        variant={invitation.entered_at ? 'outlined' : 'contained'}
                        color="primary"
                        >
                        { invitation.entered_at ? (
                            <div>
                                Mark as Not Entered<br/>
                                <Typography component="span" variant="caption">
                                    Entered on {DateTime.fromISO(invitation.entered_at).toLocaleString(DateTime.DATETIME_MED)}
                                </Typography>
                            </div>
                        ) : (
                            <span>Enter Event</span>
                        )}
                    </Button>
                    <ButtonGroup
                        fullWidth
                        orientation="vertical"
                        color="primary"
                        className={classes.marginBottom}
                        >
                        <Button
                            variant={(invitation.status === 'accepted') ? "contained" : null}
                            onClick={postAccept}
                            >
                            Accept
                        </Button>
                        <Button
                            variant={(invitation.status === 'maybe') ? "contained" : null}
                            onClick={postMaybe}
                            >
                            Maybe
                            </Button>
                        <Button
                            variant={(invitation.status === 'withdrawn') ? "contained" : null}
                            onClick={()=>postWithdraw(invitation.id)}
                            >
                            Withdraw
                        </Button>
                        <Button
                            variant={(invitation.status === 'declined') ? "contained" : null}
                            onClick={postDecline}
                            >
                            Decline
                        </Button>
                    </ButtonGroup>
                    <RedButton
                        fullWidth
                        variant={(invitation.flagged) ? "contained" : "outlined"}
                        onClick={postFlagged}
                        className={classes.marginBottom}
                        >
                        {invitation.flagged ? "Unflag" : "Flag Invitation"}
                    </RedButton>
                    <RedButton 
                        fullWidth
                        onClick={openDeleteInvitationDialog}
                        variant="outlined"
                        >
                        Delete Invitation
                    </RedButton>
                </Grid>
            </Grid>
            <EditInvitationDialog
                open={editInvitationDialogOpen}
                invitation={invitation}
                tickets={event.tickets}
                onClose={closeEditInvitationDialog}
                onSave={closeEditInvitationDialogAndRefresh}
            />
            <EditDateDialog
                invitation={invitation}
                initDate={activeDate}
                open={editDateDialogOpen}
                onClose={closeEditDateDialog}
                onSave={closeEditDateDialogAndRefresh}
                onClosed={editDateDialogClosed}
                admin
            />
            <ChargePaymentMethodDialog
                user={invitation.guest}
                club={app.club}
                invoice={activeInvoice}
                urlPrefix={`/admin/user/${invitation.guest.id}`}
                open={chargePaymentMethodDialogOpen}
                onClose={closeChargePaymentMethodDialog}
                onClosed={chargePaymentMethodDialogClosed}
                onCharge={closeChargePaymentMethodDialogAndRefresh}
            />
            {(invitation.invitable_type === 'App\\Models\\User') &&
                <ChargePaymentMethodDialog
                    user={invitation.invitable}
                    club={app.club}
                    invoice={activeInvoice}
                    urlPrefix={`/admin/user/${invitation.invitable.id}`}
                    open={chargeInviterPaymentMethodDialogOpen}
                    onClose={closeChargeInviterPaymentMethodDialog}
                    onClosed={chargeInviterPaymentMethodDialogClosed}
                    onCharge={closeChargeInviterPaymentMethodDialogAndRefresh}
                />
            }
            <ChargePaymentMethodDialog
                user={invitation.guest}
                club={app.club}
                invoice={activeInvoice}
                urlPrefix={`/admin/user/${invitation.guest.id}`}
                open={chargePaymentMethodDialogOpen}
                onClose={closeChargePaymentMethodDialog}
                onClosed={chargePaymentMethodDialogClosed}
                onCharge={closeChargePaymentMethodDialogAndRefresh}
            />
            <Dialog
                open={deleteInvitationDialogOpen}
                onClose={closeDeleteInvitationDialog}
                fullWidth
                maxWidth="sm"
                aria-labelledby="delete-rec"
                >
                <DialogTitle id="delete-rec">
                    Delete Invitation
                </DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this invitation? Any invoices attached to this invitation should be managed in <strong>invoices</strong> first.
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={closeDeleteInvitationDialog}>
                        Cancel
                    </Button>
                    <RedButton variant="contained" onClick={deleteInvitation}>
                        Delete Invitation
                    </RedButton>
                </DialogActions>
            </Dialog>
        </div>
    ) : (
        <div className={classes.emptyContainer}>
            <Loading isLoading={true}/>
        </div>
    )
}
